"use client";

import { memo } from "react";
import { useScenePause } from "./useScenePause";
import { useT } from "@/lib/locale-context";

/**
 * Animated SVG illustration for the "Analyze by URL" card.
 *
 * Reads as "drop in a listing → the AI scans it → confident deal verdict."
 * A listing card sits in frame (a vibrant property thumbnail + skeleton
 * title lines); a glowing gradient SCAN BEAM sweeps down it while an AI
 * sparkle pulses, then a vibrant emerald verdict row reveals at the bottom
 * (check badge + "Good deal" + "−12% under market").
 *
 * Vibrancy note: earlier this illustration was an all-muted grey wireframe
 * (address-bar pill + a thin progress bar + an outline verdict card) that
 * read as flat next to its livelier siblings. The redesign keeps the SURFACE
 * on the theme tokens (so it still auto-flips in dark mode) but moves the
 * meaning onto VIBRANT gradient accents - an indigo→violet photo tile, a
 * cyan/indigo scan beam, an emerald verdict badge, an amber AI sparkle -
 * the same "vibrant shape on a theme-safe neutral" approach the tool
 * illustrations use.
 *
 * All pure SVG + CSS keyframes, so the home page pays no runtime cost.
 * memo()-wrapped: the component takes no props, so every parent render
 * (e.g. the homepage's `isMobile` flag flipping at the 640px breakpoint
 * during a resize drag) would otherwise re-reconcile the whole SVG tree.
 */
function AnalyzeAnimation() {
  const sceneRef = useScenePause<SVGSVGElement>();
  const t = useT();

  // Card geometry - keeps the photo / skeleton / beam / verdict aligned
  // if sizes are ever retuned.
  const CARD_X = 22;
  const CARD_Y = 22;
  const CARD_W = 156;
  const CARD_H = 104;
  const BEAM_H = 18;
  const BEAM_TOP = CARD_Y;             // beam starts at the card's top edge
  const BEAM_BOTTOM = CARD_Y + CARD_H - BEAM_H; // ...sweeps to the bottom

  // Theme tokens for the SURFACE only (auto-flip in dark mode).
  const C_PAPER = "var(--color-paper-soft)";
  const C_RULE = "var(--color-rule)";
  const C_INK = "var(--color-ink)";
  const C_INK_FAINT = "var(--color-ink-faint)";

  // Vibrant accent hexes (mid-tones that read on both light + dark wells,
  // matching the tool illustrations' palette discipline).
  const INDIGO = "#6366f1";
  const EMERALD = "#10b981";
  const AMBER = "#f59e0b";

  return (
    <svg ref={sceneRef} viewBox="0 0 200 160" className="scene-svg w-full h-44" aria-hidden="true">
      <defs>
        <linearGradient id="a-photo" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#6366f1" />
          <stop offset="1" stopColor="#8b5cf6" />
        </linearGradient>
        <linearGradient id="a-badge" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#34d399" />
          <stop offset="1" stopColor="#059669" />
        </linearGradient>
        {/* A vibrant beam: transparent edges, bright middle, so it reads as
            a focused scan line rather than a solid bar. */}
        <linearGradient id="a-beam" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#22d3ee" stopOpacity="0" />
          <stop offset="0.5" stopColor="#818cf8" stopOpacity="0.95" />
          <stop offset="1" stopColor="#22d3ee" stopOpacity="0" />
        </linearGradient>
        <clipPath id="a-cardClip">
          <rect x={CARD_X} y={CARD_Y} width={CARD_W} height={CARD_H} rx="14" />
        </clipPath>
      </defs>

      {/* Listing card surface (theme-safe). */}
      <rect x={CARD_X} y={CARD_Y} width={CARD_W} height={CARD_H} rx="14" fill={C_PAPER} stroke={C_RULE} strokeWidth="1" />

      {/* Photo thumbnail - vibrant indigo→violet tile with a white skyline. */}
      <rect x="34" y="34" width="44" height="46" rx="9" fill="url(#a-photo)" />
      <g fill="#ffffff" fillOpacity="0.92">
        <rect x="44" y="50" width="11" height="22" rx="1.5" />
        <rect x="57" y="44" width="12" height="28" rx="1.5" />
      </g>
      <g fill={INDIGO}>
        {/* shorter tower windows */}
        <rect x="46.5" y="54" width="2.5" height="2.5" /><rect x="50.5" y="54" width="2.5" height="2.5" />
        <rect x="46.5" y="60" width="2.5" height="2.5" /><rect x="50.5" y="60" width="2.5" height="2.5" />
        {/* taller tower windows */}
        <rect x="59.5" y="48" width="2.5" height="2.5" /><rect x="64" y="48" width="2.5" height="2.5" />
        <rect x="59.5" y="54" width="2.5" height="2.5" /><rect x="64" y="54" width="2.5" height="2.5" />
        <rect x="59.5" y="60" width="2.5" height="2.5" /><rect x="64" y="60" width="2.5" height="2.5" />
      </g>

      {/* Skeleton title + meta lines (the listing's text). */}
      <rect x="86" y="42" width="72" height="8" rx="4" fill={C_RULE} />
      <rect x="86" y="56" width="54" height="8" rx="4" fill={C_RULE} />
      <rect x="86" y="70" width="40" height="7" rx="3.5" fill={C_RULE} fillOpacity="0.6" />

      {/* AI sparkle - pulses while the beam is scanning. */}
      <path
        className="a-spark"
        d="M162 27 l1.7 4 l4 1.7 l-4 1.7 l-1.7 4 l-1.7 -4 l-4 -1.7 l4 -1.7 Z"
        fill={AMBER}
      />

      {/* Scan beam - glowing gradient bar that sweeps top→bottom, clipped to
          the card's rounded corners. */}
      <g clipPath="url(#a-cardClip)">
        <rect className="a-beam" x={CARD_X} y={BEAM_TOP} width={CARD_W} height={BEAM_H} fill="url(#a-beam)" />
      </g>

      {/* Verdict banner - an emerald-tinted result strip with a deal badge,
          label + "under market" sub. Reveals (fade + rise) as ONE group once
          the scan completes, so nothing swoops in off-pivot. */}
      <g className="a-verdict">
        <rect x="30" y="90" width="140" height="28" rx="9" fill={EMERALD} fillOpacity="0.1" />
        <rect x="37" y="94" width="20" height="20" rx="6" fill="url(#a-badge)" />
        <path
          d="M42 104 l3 3 l6 -7"
          stroke="#ffffff"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <text x="64" y="101" fill={C_INK} fontSize="10.5" fontWeight="600">
          {t("illust.verdict.title")}
        </text>
        <text x="64" y="112" fill={EMERALD} fontSize="8.5" fontWeight="600">
          {t("illust.verdict.sub")}
        </text>
      </g>

      {/* "AI analyzing" caption - visible only during the scan. */}
      <text className="a-caption" x="100" y="146" textAnchor="middle" fill={C_INK_FAINT} fontSize="7.5" fontWeight="500" letterSpacing="1">
        {t("illust.analyzing")}
      </text>

      <style>{`
        /* 5s loop, ordered so every cycle reads strictly scan -> verdict
           (the scan must clearly come FIRST, and the verdict must fully
           clear before the next cycle's scan begins):
             0-32%   beam sweeps the card top→bottom (the "scan") -
                     starts immediately, sparkle + caption on
             32-38%  beam/sparkle/caption clear
             44-54%  verdict reveals
             54-88%  hold
             88-96%  verdict fades; 96-100% blank before the next scan */
        @keyframes aBeam {
          0%        { opacity: 0; transform: translateY(0); }
          4%        { opacity: 1; }
          32%       { opacity: 1; transform: translateY(${BEAM_BOTTOM - BEAM_TOP}px); }
          38%, 100% { opacity: 0; transform: translateY(${BEAM_BOTTOM - BEAM_TOP}px); }
        }
        /* gentle twinkle while scanning */
        @keyframes aSpark {
          0%        { opacity: 0; transform: scale(0.6) rotate(0deg); }
          5%        { opacity: 1; transform: scale(0.95) rotate(0deg); }
          16%       { opacity: 1; transform: scale(1.18) rotate(20deg); }
          27%       { opacity: 1; transform: scale(0.95) rotate(0deg); }
          32%       { opacity: 1; transform: scale(1.18) rotate(20deg); }
          38%, 100% { opacity: 0; transform: scale(0.6) rotate(0deg); }
        }
        @keyframes aCaption {
          0%        { opacity: 0; }
          5%, 30%   { opacity: 1; }
          38%, 100% { opacity: 0; }
        }
        @keyframes aVerdict {
          0%, 42%   { opacity: 0; transform: translateY(7px); }
          54%, 88%  { opacity: 1; transform: translateY(0); }
          96%, 100% { opacity: 0; transform: translateY(7px); }
        }
        .a-beam    { transform-box: fill-box; animation: aBeam 5s cubic-bezier(0.4,0,0.2,1) infinite; }
        .a-spark   { transform-box: fill-box; transform-origin: center; animation: aSpark 5s ease-in-out infinite; }
        .a-caption { animation: aCaption 5s ease-in-out infinite; }
        .a-verdict { transform-box: fill-box; animation: aVerdict 5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .a-beam, .a-spark, .a-caption { animation: none; opacity: 0; }
          .a-verdict { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </svg>
  );
}

export default memo(AnalyzeAnimation);
